// Ported from files/snapdraw-design-kit/code/src/components/snapdraw/auth-layout.tsx
// (Lovable reference implementation), now with the kit's bilingual EN/ES
// wiring: static copy runs through useT(), and the language pill sits in
// the top-right corner as shipped.
// 2026-08-28 (backlog LAUNCH-5): the third stat was "OFFLINE / Field mode" —
// no offline mode exists (no service worker/caching). Replaced with "PHONE /
// Field ready" (Lovable-recommended honest copy, same string length so
// nothing reflows) — see auth.stat.field in @/lib/i18n/strings/auth.ts for
// the translated label half.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { BrandMark } from "@/components/snapdraw/brand";
import { LanguageToggle } from "@/components/snapdraw/language-toggle";
import { cn } from "@/lib/utils";
import { TERMS_URL, PRIVACY_URL } from "@/lib/config";
import { useT } from "@/lib/i18n";

const STATS = [
  { value: "2 MIN", key: "auth.stat.quote" },
  { value: "G702/703", key: "auth.stat.gc" },
  { value: "PHONE", key: "auth.stat.field" },
];

/**
 * Shared split-screen shell for the unauthenticated screens
 * (login, sign up, forgot password). Brand panel left, card right.
 */
export function AuthLayout({
  eyebrow,
  children,
  footer,
}: {
  eyebrow: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const t = useT();

  return (
    <div className="sd-preview blueprint-field relative min-h-screen overflow-hidden">
      <span className="pointer-events-none absolute left-8 top-8 h-10 w-10 border-l border-t border-border-strong/60" />
      <span className="pointer-events-none absolute bottom-8 left-8 h-10 w-10 border-b border-l border-border-strong/60" />
      <div className="absolute right-5 top-5 z-10 lg:right-8 lg:top-8">
        <LanguageToggle />
      </div>

      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-12 px-6 py-14 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <section className="flex flex-col items-center text-center lg:items-start lg:text-left">
          <BrandMark size={96} />
          <h1 className="mt-8 text-[3.5rem] leading-[0.9] lg:text-[7rem]">SnapDraw</h1>
          <p className="sd-label mt-4 text-xs">{t("auth.sub")}</p>

          <div className="mt-8 hidden h-px w-40 bg-border-strong lg:block" />

          <p className="mt-6 max-w-md text-base text-foreground/80 lg:text-lg">{t("auth.tagline")}</p>

          <dl className="mt-10 flex flex-wrap justify-center gap-x-10 gap-y-6 divide-border lg:justify-start">
            {STATS.map((s) => (
              <div key={s.key}>
                <dt className="sd-numeric text-2xl font-bold lg:text-3xl">{s.value}</dt>
                <dd className="sd-label mt-1">{t(s.key)}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="sd-card w-full p-6 backdrop-blur-sm lg:p-10">
          <h2 className="sd-label">{eyebrow}</h2>
          {children}
          {footer}
          <p className="sd-label mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-[10px]">
            <span>{t("auth.copyright")}</span>
            <span aria-hidden>·</span>
            <Link to={TERMS_URL} className="text-muted-foreground hover:text-arc">
              {t("legal.terms")}
            </Link>
            <span aria-hidden>·</span>
            <Link to={PRIVACY_URL} className="text-muted-foreground hover:text-arc">
              {t("legal.privacy")}
            </Link>
          </p>
        </section>
      </div>
    </div>
  );
}

export const authFieldClass =
  "h-14 w-full rounded-xl border border-border bg-black/40 pl-12 pr-4 text-base text-foreground placeholder:text-muted-foreground focus:border-arc";

export function authFieldClassName(invalid?: boolean) {
  return cn(authFieldClass, invalid && "border-destructive focus:border-destructive");
}

export const authPrimaryClass =
  "mt-2 h-14 w-full rounded-xl bg-[image:var(--gradient-arc)] font-display text-2xl uppercase tracking-wide text-primary-foreground transition-shadow hover:glow-arc disabled:cursor-not-allowed disabled:bg-none disabled:bg-surface-raised disabled:text-muted-foreground";

export const authGhostClass =
  "h-14 w-full rounded-xl border border-arc/60 font-display text-2xl uppercase tracking-wide text-arc hover:glow-arc";
